<template>
  <div id="trackinfo">
    <div class="title-bar">
      <div class="input-group-add">
        <h1><slot name="title">Default Title</slot></h1>
        <div style="display: flex">
          <button
            type="button"
            class="button-search-border"
            @click="exportToExcel"
            v-if="exportEnable"
          >
            {{ t('export') }}
          </button>
        </div>
      </div>
      <slot name="graphic" />
      <div class="sarch-input-group">
        <label class="label" />
        <input type="text" class="sarch-input-add" v-model="search" @keyup.enter="handleEnter" />
        <button type="button" class="button-search" @click="handleEnter">{{ t('search') }}</button>
      </div>
      <div class="datepicker-bar">
        <label for="checkbox"><input type="checkbox" id="checkbox" v-model="checked" /></label>
        {{ $t('startTime') }}:
        <Datepicker v-model="start" :minimum-view="'time'" :inputFormat="'yyyy-MM-dd HH:mm'" />
        &nbsp;&nbsp;&nbsp;{{ $t('endTime') }}:
        <Datepicker v-model="end" :minimum-view="'time'" :inputFormat="'yyyy-MM-dd HH:mm'" />
        <div class="select-bar">
          {{ $t('rowCount') }}:
          <select v-model="row">
            <option v-for="option in dropdownOptions" :value="option.value" :key="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="datepicker-bar" v-if="modeEnable">
        <label for="checkbox"><input type="checkbox" id="checkbox" v-model="mode" /></label>
        <slot name="modelabel">Default Title</slot>
      </div>
      <div>{{ $t('searchCount') }}:{{ count }}</div>
    </div>
    <slot name="header" />
    <div class="items-container">
      <div v-if="items && items.length > 0" class="items-list">
        <div
          v-for="(item, index) in items"
          :key="item.id"
          :class="{ 'even-background': index % 2 === 0 }"
        >
          <slot name="item" :item="item" :index="index" @remove-item="removeItem" />
        </div>
      </div>
      <div v-else class="no-items">{{ $t('noData') }}</div>
    </div>
    <div class="pagination-container" v-if="items && items.length > 0">
      <div class="pagination" v-if="items && items.length > 0">
        <span
          v-for="p in pageRange"
          :key="p"
          @click="goToPage(p)"
          :class="{ active: p === currentPage }"
          tabindex="0"
          @keyup.enter="goToPage(p)"
        >{{ p }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  ref, inject, watch, computed,
} from 'vue';
import Swal from 'sweetalert2';
import Datepicker from 'vue3-datepicker';
import * as XLSX from 'xlsx';
import { fetchList } from '../utils/api';
import { getId } from '../../User';

export default {
  name: 'GenericList',
  components: {
    Datepicker,
  },
  data() {
    return {
      dropdownOptions: [
        { label: '25', value: 0 },
        { label: '50', value: 1 },
        { label: '75', value: 2 },
        { label: '100', value: 3 },
        { label: this.$t('unpaged'), value: 4 },
      ],
    };
  },
  props: {
    apiEndpoint: {
      type: String,
      required: true,
    },
    exportEnable: {
      type: Boolean,
      required: false,
      default: false,
    },
    apiEndpoint2: {
      type: String,
      required: false,
      default: null,
    },
    data: {
      type: String,
      required: false,
      default: null,
    },
    modeEnable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const items = ref([]);
    const route = useRoute();
    const apiBaseUrl = inject('apiBaseUrl');
    const { t } = useI18n();
    const count = ref(0);
    const row = ref(0);
    const page = ref(0);
    const currentPage = ref(0);
    const type = ref(0);
    const search = ref('');
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const start = ref(startDate);
    const end = ref(endDate);
    const checked = ref(false);
    const mode = ref(false);
    function isValidUnixTimestamp(value) {
      if (!value) return null;
      const timestamp = parseInt(value, 10);
      const date = new Date(timestamp * 1000);
      if (Number.isNaN(timestamp)) {
        return null;
      }
      return date;
    }

    function updateDates() {
      if (!checked.value) return;
      const startParam = isValidUnixTimestamp(route.params.start);
      const endParam = isValidUnixTimestamp(route.params.end);

      if (startParam && endParam) {
        start.value = startParam;
        end.value = endParam;
      } else {
        checked.value = false;
      }
    }

    async function searchList() {
      updateDates();
      checked.value = route.params.checked === '1';
      mode.value = route.params.mode === '1';
      search.value = decodeURIComponent(search.value);
      if (!Number.isNaN(parseFloat(route.params.type)) && Number.isFinite(route.params.type)) {
        type.value = route.params.type;
      }
      if (!Number.isNaN(parseFloat(route.params.page)) && Number.isFinite(route.params.page)) {
        currentPage.value = route.params.page;
      }
      const sunixtime = Math.floor(start.value / 1000);
      const eunixtime = Math.floor(end.value / 1000);
      let filed = {
        search: search.value,
        page: currentPage.value,
        type: type.value,
        checked: checked.value ? 1 : 0,
        start: sunixtime,
        end: eunixtime,
        row: row.value,
        sid: getId(),
        mode: mode.value ? 1 : 0,
      };
      console.log(filed);
      if (props.data) {
        const dataObject = JSON.parse(props.data);
        filed = {
          ...filed,
          ...dataObject,
        };
      }
      const data = JSON.stringify(filed);
      fetchList(t, `${apiBaseUrl}/search/${props.apiEndpoint}`, data).then((response) => {
        items.value = response.data.result.list;
        count.value = response.data.result.count;
        page.value = response.data.result.page;
        currentPage.value = parseInt(route.params.page, 10);
        emit('search-result', items.value);
      });
    }

    watch(
      () => [route.params.type, route.params.page, route.params.search],
      async ([newType, newPage, newSearch]) => {
        type.value = newType || 0;
        currentPage.value = newPage || 1;
        search.value = newSearch || '';
        await searchList();
      },
      {
        immediate: true,
      },
    );

    // onMounted(searchList);
    const pageRange = computed(() => {
      if (page.value <= 6) {
        return Array.from({ length: page.value }, (_, i) => i + 1);
      }
      const range = Array.from({ length: 6 }, (_, i) => i + currentPage.value);
      if (currentPage.value === 1) {
        return [...range, '...', page.value];
      }

      let maxRight = currentPage.value + 2 < page.value ? currentPage.value + 2 : page.value;
      let maxLeft = maxRight - 4 < 2 ? 2 : maxRight - 4;
      if (maxRight - 4 < 2) {
        maxLeft = 2;
        maxRight = maxLeft + 4;
      }
      if (maxRight === page.value) {
        maxRight -= 1;
        maxLeft = maxRight - 4;
      }
      return [
        1,
        '...',
        ...Array.from({ length: maxRight - maxLeft + 1 }, (_, i) => maxLeft + i),
        '...',
        page.value,
      ];
    });
    return {
      t,
      items,
      apiBaseUrl,
      count,
      pageRange,
      page,
      type,
      search,
      searchList,
      updateDates,
      currentPage,
      start,
      end,
      checked,
      row,
    };
  },
  methods: {
    removeItem(itemToRemove) {
      const index = this.items.indexOf(itemToRemove);
      if (index > -1) {
        this.items.splice(index, 1);
      }
    },
    getItems() {
      return this.items;
    },
    handleEnter() {
      const start = this.checked ? Math.floor(this.start / 1000) : 0;
      const end = this.checked ? Math.floor(this.end / 1000) : 0;
      const checked = this.checked ? 1 : 0;
      const mode = this.mode ? 1 : 0;
      this.page = 1;
      let search = encodeURIComponent(this.search);
      if (!this.search.trim()) {
        search = '%20';
      } else {
        search = search.replace('%20', '');
      }
      let str = this.apiEndpoint;
      if (this.apiEndpoint2) {
        str = this.apiEndpoint2;
      }
      this.$router.push(`/${str}/${this.type}/${this.page}/${search}/${checked}/${start}/${end}/${this.row}/${mode}`);
    },
    goToPage(page) {
      if (page !== '...') {
        const start = this.checked ? Math.floor(this.start / 1000) : 0;
        const end = this.checked ? Math.floor(this.end / 1000) : 0;
        const checked = this.checked ? 1 : 0;
        const mode = this.mode ? 1 : 0;
        this.page = this.page === 0 ? 1 : this.page;
        let search = encodeURIComponent(this.search);
        if (!this.search.trim()) {
          search = '%20';
        } else {
          search = search.replace('%20', '');
        }
        let str = this.apiEndpoint;
        if (this.apiEndpoint2) {
          str = this.apiEndpoint2;
        }
        this.$router.push(`/${str}/${this.type}/${page}/${search}/${checked}/${start}/${end}/${this.row}/${mode}`);
        this.currentPage = Number(page);
      }
    },
    exportToExcel() {
      Swal.fire({
        icon: 'warning',
        title: this.$t('confirmExport'),
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          const ws = XLSX.utils.json_to_sheet(this.items);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Data');
          XLSX.writeFile(wb, 'data.xlsx');
        }
      });
    },
  },
};
</script>

<style>
.items-container {
  height: auto;
}
.items-list {
  padding-right: 10px;
}
.even-background {
  background-color: #eeeeee;
}
.no-items {
  text-align: center;
}
.sarch-input-group {
  display: flex;
  flex-direction: row;
}
.sarch-input-add {
  flex: 1;
  outline-style: none;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 10px;
  font-size: 20px;
}
.button-search {
  background-color: #007bff;
  color: white;
  width: 100px;
  height: 45px;
  border: 0;
  font-size: 16px;
  border-radius: 5px;
  margin-left: 10px;
  margin-right: 10px;
}

.button-search-border {
  color: #007bff;
  width: 100px;
  height: 45px;
  border: 1px solid #007bff;
  font-size: 16px;
  border-radius: 5px;
  margin-left: 10px;
  margin-right: 10px;
}
.pagination-container {
  display: flex;
  justify-content: flex-end;
}

.pagination {
  margin-top: 40px;
}

.pagination span {
  cursor: pointer;
  margin-right: 5px;
  padding: 5px 10px;
  border-radius: 3px;
  background-color: #f0f0f0;
  color: #333;
}

.pagination span.active {
  background-color: #007bff;
  color: #fff;
}
.datepicker-bar {
  padding-top: 10px;
  display: flex;
  width: 100%;
  padding-bottom: 10px;
  align-items: center;
}
.select-bar {
  padding-left: 10px;
  display: flex;
  align-items: center;
  justify-items: center;
}
.select-bar > select {
  margin-top: 1px;
  height: 20px
}
</style>
